import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronUp } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Routes, Route } from 'react-router-dom';

import analytics from './utils/analytics';
import {
  getAnalysesMeterParts,
} from './utils/getTotalAnalysesRemainingParts';
import { validateUploadForm } from './utils/validateUploadForm';
import { useSessionState } from './hooks/useSessionState';
import { useHomeCheckout } from './hooks/useHomeCheckout';
import { useAnalyzeSubmit } from './hooks/useAnalyzeSubmit';

import AppShell from './components/layout/AppShell';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import CraftTypeToggle from './components/analysis/CraftTypeToggle';
import UploadForm from './components/analysis/UploadForm';
import AnalysisResults from './components/analysis/AnalysisResults';
import PricingSection from './components/billing/PricingSection';
import SuccessPage from './components/SuccessPage';
import TestimonialSlideshow from './components/marketing/TestimonialSlideshow';
import TeamSection from './components/marketing/TeamSection';
import CraftsIntro from './components/marketing/CraftsIntro';
import FeaturedOn from './components/marketing/FeaturedOn';
import ReviewsCount from './components/marketing/ReviewsCount';
import BackedBy from './components/marketing/BackedBy';
import { Eyebrow } from './components/ui';

const EMPTY_FORM = {
  trackTitle: '',
  artistName: '',
  lyrics: '',
  isInstrumental: false,
  bookTitle: '',
  authorName: '',
  manuscript: '',
  genre: '',
  subgenre: '',
  targetAudience: '',
};

function App() {
  const [craftType, setCraftType] = useState('music');
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [sessionStartTime] = useState(Date.now());
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [user, setUser] = useState(null);
  const pricingSectionRef = useRef(null);

  const {
    submissionCount,
    setSubmissionCount,
    paidAnalyses,
    setPaidAnalyses,
    paidAnalysesHighWater,
    setPaidAnalysesHighWater,
    setLastDailyAnalysis,
    dailyAnalysesAvailable,
    setDailyAnalysesAvailable,
    sessionUser,
    hasActiveSubscription,
    setHasActiveSubscription,
    subscriptionAnalysesUsed,
    setSubscriptionAnalysesUsed,
    subscriptionAnalysesLimit,
    setSubscriptionAnalysesLimit,
    syncSessionState,
  } = useSessionState();

  const hasPremiumAccess = hasActiveSubscription || paidAnalyses > 0;
  const showPremiumContent = hasPremiumAccess || (results && results.usedPaidAnalysisForThisRun);

  const billingAccountUser = useMemo(() => {
    if (sessionUser?.email && sessionUser?.id) return sessionUser;
    if (user?.email && user?.id) return user;
    return null;
  }, [sessionUser, user]);

  const analysesMeter = useMemo(
    () =>
      getAnalysesMeterParts({
        hasActiveSubscription,
        subscriptionAnalysesLimit,
        subscriptionAnalysesUsed,
        paidAnalyses,
        paidAnalysesHighWater,
        submissionCount,
        dailyAnalysesAvailable,
      }),
    [
      hasActiveSubscription,
      subscriptionAnalysesLimit,
      subscriptionAnalysesUsed,
      paidAnalyses,
      paidAnalysesHighWater,
      submissionCount,
      dailyAnalysesAvailable,
    ]
  );
  const totalAnalysesRemaining = analysesMeter.remaining;
  const analysesBarPercent = analysesMeter.meter.percent;
  const analysesMeterMax = analysesMeter.meter.max;

  const scrollToPricingSection = useCallback((message, variant = 'info') => {
    if (message) {
      if (variant === 'error') {
        toast.error(message);
      } else {
        toast(message);
      }
    }
    requestAnimationFrame(() => {
      pricingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const checkout = useHomeCheckout({
    billingAccountUser,
    user,
    pricingSectionRef,
    hasActiveSubscription,
  });

  const validateForm = useCallback(() => {
    return validateUploadForm({ craftType, selectedFile, formData });
  }, [selectedFile, formData, craftType]);

  const { handleSubmit } = useAnalyzeSubmit({
    craftType,
    formData,
    setFormData,
    selectedFile,
    error,
    setError,
    setIsLoading,
    setUploadProgress,
    setResults,
    totalAnalysesRemaining,
    paidAnalyses,
    setPaidAnalyses,
    paidAnalysesHighWater,
    setPaidAnalysesHighWater,
    submissionCount,
    setSubmissionCount,
    hasActiveSubscription,
    setHasActiveSubscription,
    subscriptionAnalysesUsed,
    setSubscriptionAnalysesUsed,
    subscriptionAnalysesLimit,
    setSubscriptionAnalysesLimit,
    setLastDailyAnalysis,
    setDailyAnalysesAvailable,
    dailyAnalysesAvailable,
    syncSessionState,
    scrollToPricingSection,
    validateForm,
  });

  useEffect(() => {
    analytics.trackPageView('CriticAI - Craft Analysis');
    return () => {
      analytics.trackSessionDuration(Date.now() - sessionStartTime);
    };
  }, [sessionStartTime]);

  useEffect(() => {
    if (results) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      analytics.trackAnalysisResultViewed(craftType);
    }
  }, [results, craftType]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollToTop(scrollTop > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === '#success') {
        setShowSuccessPage(true);
        const searchParams = window.location.search;
        window.history.replaceState(null, null, window.location.pathname + searchParams);
      } else {
        setShowSuccessPage(false);
      }
    };
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleAuthChange = useCallback(
    (authUser) => {
      setUser(authUser);
      syncSessionState();
    },
    [syncSessionState]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // Parent genre change clears subgenre — options are parent-scoped.
      if (name === 'genre') next.subgenre = '';
      return next;
    });
    if (error) setError('');
    analytics.trackFormInteraction(name, 'input_change');
  };

  const handleInstrumentalChange = (checked) => {
    setFormData((prev) => ({ ...prev, isInstrumental: checked }));
    if (error) setError('');
  };

  const handleCraftTypeChange = (newType) => {
    setCraftType(newType);
    setSelectedFile(null);
    setFormData(EMPTY_FORM);
    setError('');
    setResults(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (craftType === 'music') {
      const allowedTypes = ['audio/mp3', 'audio/wav', 'audio/mpeg', 'audio/flac', 'audio/x-flac'];
      const allowedExtensions = /\.(mp3|wav|flac)$/i;
      const isValidType = allowedTypes.includes(file.type) || allowedExtensions.test(file.name);
      const isValidSize = file.size <= 25 * 1024 * 1024;

      if (isValidType && isValidSize) {
        setSelectedFile(file);
        setError('');
        analytics.trackFileSelection(file.type || file.name.split('.').pop(), file.size);
        toast.success(`Audio file selected: ${file.name}`);
      } else if (!isValidType) {
        setError('Please select a valid audio file (MP3, WAV, or FLAC).');
        setSelectedFile(null);
        analytics.trackAnalysisError('invalid_file_type', 'Invalid file type selected');
      } else {
        setError('File size too large. Please select a file smaller than 25MB.');
        setSelectedFile(null);
        analytics.trackAnalysisError('file_too_large', 'File size exceeds limit');
      }
    } else {
      const allowedTypes = [
        'text/plain',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      const allowedExtensions = /\.(txt|pdf|doc|docx)$/i;
      const isValidType = allowedTypes.includes(file.type) || allowedExtensions.test(file.name);
      const isValidSize = file.size <= 10 * 1024 * 1024;

      if (isValidType && isValidSize) {
        setSelectedFile(file);
        setError('');
        setResults(null);
        setFormData((prev) => ({ ...prev, manuscript: '' }));
        analytics.trackFileSelection(file.type || file.name.split('.').pop(), file.size);
        toast.success(`Book file selected: ${file.name}`);
      } else if (!isValidType) {
        setError('Please select a valid book file (TXT, PDF, DOC, or DOCX).');
        setSelectedFile(null);
        analytics.trackAnalysisError('invalid_file_type', 'Invalid file type selected');
      } else {
        setError('File size too large. Please select a file smaller than 10MB.');
        setSelectedFile(null);
        analytics.trackAnalysisError('file_too_large', 'File size exceeds limit');
      }
    }
  };

  const handleBackToForm = () => {
    setResults(null);
    setSelectedFile(null);
    setFormData(EMPTY_FORM);
    setError('');
    analytics.trackButtonClick('back_to_form', 'results_page');
    if (totalAnalysesRemaining <= 0) {
      scrollToPricingSection(
        'Purchase credits or subscribe below to run more analyses. You will be redirected to Stripe checkout.',
        'info'
      );
    }
  };

  const homePage = (
    <AppShell>
      <Header
        totalAnalysesRemaining={totalAnalysesRemaining}
        analysesPercent={analysesBarPercent}
        analysesMeterMax={analysesMeterMax}
        onPurchaseClick={checkout.scrollToPurchaseCreditsSection}
        onAuthChange={handleAuthChange}
        showManageSubscription={checkout.showManageSubscription}
        onManageSubscription={checkout.handleManageSubscription}
        user={user}
      />

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 md:pt-16 pb-24">
        {!results ? (
          <div
            className={`animate-fade-in transition-all duration-500 ${
              craftType === 'music' ? 'animate-slide-in-left' : 'animate-slide-in-right'
            }`}
          >
            <div className="text-center mb-12 md:mb-14 max-w-4xl mx-auto critic-stagger">
              <Eyebrow className="mb-5">Analysis, not hype</Eyebrow>
              <h2 className="font-display text-5xl sm:text-6xl md:text-7xl font-bold text-gradient-insight mb-6 md:mb-7 leading-[1.05] tracking-tight px-2">
                Analyze Your Craft
              </h2>
              <p className="text-lg md:text-xl text-critic-text-secondary max-w-2xl mx-auto leading-relaxed font-normal px-4">
                Objective, structured feedback on music tracks and book manuscripts for emerging creatives.
              </p>
            </div>

            <CraftTypeToggle craftType={craftType} onCraftTypeChange={handleCraftTypeChange} />

            <UploadForm
              craftType={craftType}
              formData={formData}
              onInputChange={handleInputChange}
              onInstrumentalChange={handleInstrumentalChange}
              selectedFile={selectedFile}
              onFileSelect={handleFileSelect}
              onSubmit={handleSubmit}
              isLoading={isLoading}
              uploadProgress={uploadProgress}
              error={error}
            />

            <CraftsIntro />
            <ReviewsCount />
            <FeaturedOn />
            <TestimonialSlideshow />

            <PricingSection
              ref={pricingSectionRef}
              homePaymentType={checkout.homePaymentType}
              setHomePaymentType={checkout.setHomePaymentType}
              homePpuQuantity={checkout.homePpuQuantity}
              setHomePpuQuantity={checkout.setHomePpuQuantity}
              homeSelectedSubTier={checkout.homeSelectedSubTier}
              setHomeSelectedSubTier={checkout.setHomeSelectedSubTier}
              homePromoCode={checkout.homeCouponCode}
              setHomePromoCode={checkout.setHomeCouponCode}
              homeAppliedCoupon={checkout.homeAppliedCoupon}
              homeCouponError={checkout.homeCouponError}
              homeCouponLoading={checkout.homeCouponLoading}
              homeCheckoutLoading={checkout.homeCheckoutLoading}
              onApplyCoupon={checkout.validateHomeCoupon}
              onRemoveCoupon={checkout.removeHomeCoupon}
              onCheckout={checkout.handleHomeStripeCheckout}
              accountUser={billingAccountUser}
              homeSelectedTierData={checkout.homeSelectedTierData}
              homeSelectedSubPlan={checkout.homeSubPlan}
            />

            <BackedBy />
          </div>
        ) : (
          <AnalysisResults
            results={results}
            craftType={craftType}
            showPremiumContent={showPremiumContent}
            hasPremiumAccess={hasPremiumAccess}
            onBack={handleBackToForm}
          />
        )}
      </main>

      <TeamSection />
      <Footer />

      {showScrollToTop && results && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 z-50 w-12 h-12 bg-gradient-to-r from-apple-blue to-apple-purple rounded-full flex items-center justify-center text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 backdrop-blur-xl border border-white/20 animate-bounce-in scroll-to-top-btn"
          aria-label="Scroll to top"
        >
          <ChevronUp className="h-6 w-6 animate-pulse" />
        </button>
      )}
    </AppShell>
  );

  return (
    <Routes>
      <Route path="/success" element={<SuccessPage />} />
      <Route path="/" element={showSuccessPage ? <SuccessPage /> : homePage} />
    </Routes>
  );
}

export default App;
